import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import useWebSocket, { ReadyState } from "react-use-websocket";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import {
  selectAuthScheme,
  selectCurrentClinic,
  selectCurrentUser,
  selectCurrentUserToken
} from "features/auth/auth-slice";
import { buildWebsocketAuthToken } from "utils/auth-header.util";
import {
  AMBIENT_SCRIBE_REFETCH,
  eventBus
} from "hooks/use-event-bus/event-bus";
import { ambientScribeApi } from "services/ambient-scribe";
import { assignedTagsApi } from "services/assigned-tags";
import { chartNotesApi } from "services/chart-notes";
import {
  useGetNotificationsQuery,
  useGetUnreadCountsQuery,
  useReadAllNotificationsMutation,
  useUpdateNotificationsMutation
} from "services/notifications";
import {
  Notification,
  NotificationFilters,
  NotificationSocketResponse,
  NotificationTypes
} from "services/types/notifications";
import { getDynamicWsUrl } from "utils/api-config.util";

import {
  AmbientScribeSoapCompletionNotificationType,
  AmbientScribeSummaryCompletionNotificationType,
  BulkAssignTagsCompletedNotificationType
} from "./use-clinic-notifications";

type UnreadCounts = {
  [key: string]: number;
  all: number;
};

const INITIAL_COUNTS: UnreadCounts = {
  all: 0,
  [NotificationTypes.REPORT]: 0,
  [NotificationTypes.MESSAGE]: 0,
  [NotificationTypes.APPOINTMENT]: 0,
  [NotificationTypes.INTAKE_FORM]: 0,
  [NotificationTypes.TASK]: 0
};

const NOTIFICATIONS_PAGE_SIZE = 50;

const useNotifications = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const token = useAppSelector(selectCurrentUserToken);
  const authScheme = useAppSelector(selectAuthScheme);
  const user = useAppSelector(selectCurrentUser);
  const currentClinic = useAppSelector(selectCurrentClinic);
  const [socketUrl, setSocketUrl] = useState("");
  const [shouldFetchNotifications, setShouldFetchNotifications] =
    useState(false);

  const { sendJsonMessage, lastJsonMessage, readyState, getWebSocket } =
    useWebSocket<NotificationSocketResponse | any>(
      socketUrl,
      { shouldReconnect: () => true },
      Boolean(socketUrl)
    );

  useEffect(() => {
    if (currentClinic?.id && user?.id && user.id !== -1) {
      setSocketUrl(
        `${getDynamicWsUrl()}/user-notifications/${user.id}/clinic/${
          currentClinic?.id
        }/`
      );
    }
  }, [user?.id, currentClinic?.id]);

  useEffect(() => {
    const ws = getWebSocket();
    if (
      readyState === ReadyState.OPEN &&
      token &&
      socketUrl &&
      ws?.readyState === WebSocket.OPEN
    ) {
      try {
        sendJsonMessage({
          method: "authenticate",
          token: buildWebsocketAuthToken(token, authScheme)
        });
      } catch (error) {
        console.error("[useNotifications] Failed to send auth message:", error);
      }
    }
  }, [readyState, sendJsonMessage, token, authScheme, socketUrl, getWebSocket]);

  const [activeType, _setActiveType] = useState<
    NotificationFilters["type"] | null
  >(null);

  const setActiveType = useCallback(
    (type: NotificationFilters["type"] | null) => {
      _setActiveType(type);
      setOffset(0);
      setAccumulatedNotifications([]);
      setHasMore(true);
    },
    []
  );

  // Delay notifications fetch on patients page to prioritize patients query
  useEffect(() => {
    const isPatientsPage =
      location.pathname === "/" || location.pathname.startsWith("/scheduling");
    const shouldDelay = isPatientsPage;

    if (
      !shouldDelay &&
      !shouldFetchNotifications &&
      currentClinic?.id &&
      user?.id
    ) {
      setShouldFetchNotifications(true);
    } else if (
      shouldDelay &&
      !shouldFetchNotifications &&
      currentClinic?.id &&
      user?.id
    ) {
      // Delay fetching notifications on patients page by 2 seconds
      const timer = setTimeout(() => {
        setShouldFetchNotifications(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [
    location.pathname,
    currentClinic?.id,
    user?.id,
    shouldFetchNotifications
  ]);

  const [offset, setOffset] = useState(0);
  const [accumulatedNotifications, setAccumulatedNotifications] = useState<
    Notification[]
  >([]);
  const [hasMore, setHasMore] = useState(true);

  const skipQuery =
    !currentClinic?.id ||
    !user?.id ||
    user.id === -1 ||
    !shouldFetchNotifications;

  const { data, refetch, isLoading, isFetching, isUninitialized } =
    useGetNotificationsQuery(
      {
        clinic: currentClinic?.id,
        provider: user.id,
        limit: NOTIFICATIONS_PAGE_SIZE,
        offset,
        ...(activeType != null && { type: activeType })
      },
      { skip: skipQuery }
    );

  const { data: unreadCountsData, refetch: refetchUnreadCounts } =
    useGetUnreadCountsQuery(
      { clinic: currentClinic?.id, provider: user.id },
      { skip: skipQuery }
    );

  useEffect(() => {
    if (!data) return;
    if (offset === 0) {
      setAccumulatedNotifications(data);
    } else {
      setAccumulatedNotifications((prev) => {
        const existingIds = new Set(prev.map((n) => n.id));
        const newItems = data.filter((n) => !existingIds.has(n.id));
        return [...prev, ...newItems];
      });
    }
    setHasMore(data.length >= NOTIFICATIONS_PAGE_SIZE);
  }, [data, offset]);

  useEffect(() => {
    if (
      lastJsonMessage?.type === "new.user.notification" ||
      lastJsonMessage?.type === "stafftaskcomment.create.notification"
    ) {
      if (!isLoading && !isUninitialized) {
        setOffset(0);
        refetch();
        refetchUnreadCounts();
      }
      if (lastJsonMessage?.message?.type === NotificationTypes.AMBIENT_SCRIBE) {
        dispatch(chartNotesApi.util.invalidateTags(["ChartNotes"]));
        eventBus.emit(AMBIENT_SCRIBE_REFETCH, undefined);
      }
    } else if (
      lastJsonMessage?.type === AmbientScribeSoapCompletionNotificationType ||
      lastJsonMessage?.type === AmbientScribeSummaryCompletionNotificationType
    ) {
      dispatch(ambientScribeApi.util.invalidateTags(["AmbientScribes"]));
      dispatch(chartNotesApi.util.invalidateTags(["ChartNotes"]));
      eventBus.emit(AMBIENT_SCRIBE_REFETCH, undefined);
    } else if (
      lastJsonMessage?.type === BulkAssignTagsCompletedNotificationType
    ) {
      dispatch(
        assignedTagsApi.util.invalidateTags(["AssignedPatientTags", "Patients"])
      );
      const patientsCount = lastJsonMessage?.message?.patients_count;
      if (patientsCount != null) {
        dispatch(
          setCustomMessage(`Tags assigned to ${patientsCount} patients`)
        );
        dispatch(setOpen(true));
      }
    }
  }, [
    dispatch,
    isLoading,
    isUninitialized,
    lastJsonMessage,
    refetch,
    refetchUnreadCounts
  ]);

  const loadMore = useCallback(() => {
    if (!isFetching && hasMore) {
      setOffset((prev) => prev + NOTIFICATIONS_PAGE_SIZE);
    }
  }, [isFetching, hasMore]);

  const [updateNotification] = useUpdateNotificationsMutation();

  const readNotification = useCallback(
    (id: number) => {
      updateNotification({ id, is_read: true });
      setAccumulatedNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      );
    },
    [updateNotification]
  );

  const [readAllNotifications] = useReadAllNotificationsMutation();

  const handleReadAllNotifications = useCallback(async () => {
    await readAllNotifications(user.id);
    setOffset(0);
    setAccumulatedNotifications([]);
  }, [user.id, readAllNotifications]);

  const deleteNotification = useCallback(
    (id: number) => {
      updateNotification({ id, is_active: false, is_read: true });
      setAccumulatedNotifications((prev) => prev.filter((n) => n.id !== id));
    },
    [updateNotification]
  );

  const refetchNotifications = useCallback(() => {
    if (!isLoading && !isUninitialized) {
      setOffset(0);
      refetch();
    }
  }, [isLoading, isUninitialized, refetch]);

  const unreadNotificationsCount = useMemo<UnreadCounts>(() => {
    if (!unreadCountsData) return INITIAL_COUNTS;

    const all = Object.values(unreadCountsData).reduce(
      (sum, count) => sum + count,
      0
    );
    return {
      ...INITIAL_COUNTS,
      ...Object.fromEntries(
        Object.entries(unreadCountsData).map(([type, count]) => [type, count])
      ),
      all
    };
  }, [unreadCountsData]);

  return {
    notifications: accumulatedNotifications,
    readNotification,
    deleteNotification,
    setActiveType,
    activeType,
    unreadNotificationsCount,
    handleReadAllNotifications,
    refetch: refetchNotifications,
    loadMore,
    hasMore,
    isLoadingMore: isFetching && offset > 0
  };
};

export default useNotifications;
